import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from 'lib/utils';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/ui/select';
import { useLanguageContext } from 'i18n/language-context';
import { AccountRow } from './account-row';
import {
  accountBadgeClassName,
  accountFieldClassName,
  accountGhostButtonClassName,
  accountPrimaryButtonClassName,
  accountSectionClassName,
  accountSectionTitleClassName,
} from './account-styles';

// Same rules as the Edit profile dialog.
const NAME_STARTS_WITH_LETTER = /^[A-Za-zÀ-ÖØ-öø-ÿ]/;

// Hidden for now; set to true to show the language picker again.
const SHOW_LANGUAGE = false;

export type ProfileDetailsChanges = { fullName?: string; language?: string };

interface ProfileDetailsCardProps {
  fullName: string;
  email: string;
  isVerified: boolean;
  isSaving: boolean;
  /** Only the fields that changed are passed. */
  onSave: (changes: ProfileDetailsChanges) => void;
}

export function ProfileDetailsCard({
  fullName,
  email,
  isVerified,
  isSaving,
  onSave,
}: ProfileDetailsCardProps) {
  const { t } = useTranslation();
  const { currentLanguage, availableLanguages } = useLanguageContext();
  const [name, setName] = useState(fullName);
  const [nameError, setNameError] = useState('');
  const [language, setLanguage] = useState(currentLanguage);

  // Show the saved values again once they reload after a save.
  useEffect(() => {
    setName(fullName);
  }, [fullName]);

  useEffect(() => {
    setLanguage(currentLanguage);
  }, [currentLanguage]);

  const trimmedName = name.trim();
  const isNameChanged = trimmedName !== fullName;
  const isLanguageChanged = language !== currentLanguage;
  const hasChanges = isNameChanged || isLanguageChanged;

  const getNameError = (value: string) => {
    if (!value) return t('FULL_NAME_REQUIRED');
    if (value.length < 2) return t('FULL_NAME_TOO_SHORT');
    if (value.length > 30) return t('FULL_NAME_TOO_LONG');
    if (!NAME_STARTS_WITH_LETTER.test(value)) return t('FULL_NAME_INVALID_FORMAT');
    return '';
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!hasChanges) return;

    const error = isNameChanged ? getNameError(trimmedName) : '';
    setNameError(error);
    if (error) return;

    onSave({
      fullName: isNameChanged ? trimmedName : undefined,
      language: isLanguageChanged ? language : undefined,
    });
  };

  const handleCancel = () => {
    setName(fullName);
    setLanguage(currentLanguage);
    setNameError('');
  };

  const fullNameLabel = t('ACCOUNT_FULL_NAME', { defaultValue: 'Full name' });
  const emailLabel = t('ACCOUNT_EMAIL', { defaultValue: 'Email' });

  return (
    <form onSubmit={handleSubmit} className={accountSectionClassName}>
      <div className="px-6 pb-2 pt-5">
        <h2 className={accountSectionTitleClassName}>
          {t('ACCOUNT_YOUR_DETAILS', { defaultValue: 'Your details' })}
        </h2>

        <AccountRow
          title={fullNameLabel}
          hint={t('ACCOUNT_FULL_NAME_HINT', { defaultValue: 'Shown to your team' })}
        >
          <div className="flex w-full flex-col gap-1">
            <input
              aria-label={fullNameLabel}
              aria-invalid={Boolean(nameError)}
              value={name}
              disabled={isSaving}
              onChange={(event) => setName(event.target.value)}
              className={cn(
                accountFieldClassName,
                'outline-none focus:border-grey-200 disabled:opacity-60',
                nameError && 'border-error-700 focus:border-error-700'
              )}
            />
            {nameError ? <p className="text-xs text-error-700">{nameError}</p> : null}
          </div>
        </AccountRow>

        <AccountRow
          title={emailLabel}
          hint={t('ACCOUNT_EMAIL_HINT', {
            defaultValue: "You sign in with this, and it's where we send updates",
          })}
          isLast={!SHOW_LANGUAGE}
        >
          {/* Read-only: the email can't be changed here. The badge sits inside the field. */}
          <div className={cn(accountFieldClassName, 'gap-2 bg-grey-50 dark:bg-muted')}>
            <input
              aria-label={emailLabel}
              value={email}
              title={email}
              disabled
              readOnly
              className="min-w-0 flex-1 cursor-not-allowed truncate bg-transparent text-grey-500 outline-none dark:text-muted-foreground"
            />
            {isVerified ? (
              <span className={cn(accountBadgeClassName, 'bg-success-50 text-success-700')}>
                {t('ACCOUNT_VERIFIED', { defaultValue: 'Verified' })}
              </span>
            ) : null}
          </div>
        </AccountRow>

        {SHOW_LANGUAGE ? (
          <AccountRow
            title={t('ACCOUNT_LANGUAGE', { defaultValue: 'Language' })}
            hint={t('ACCOUNT_LANGUAGE_HINT', { defaultValue: 'For Klax menus and emails' })}
            isLast
          >
            <Select value={language} onValueChange={setLanguage} disabled={isSaving}>
              <SelectTrigger
                aria-label={t('ACCOUNT_LANGUAGE', { defaultValue: 'Language' })}
                className={cn(accountFieldClassName, 'justify-between shadow-none focus:ring-0')}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {availableLanguages.map((item) => (
                  <SelectItem key={item.languageCode} value={item.languageCode}>
                    {t(`LANGUAGE_${item.languageCode.replace('-', '_').toUpperCase()}`, {
                      defaultValue: item.languageName,
                    })}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </AccountRow>
        ) : null}
      </div>

      {hasChanges ? (
        <div className="flex items-center justify-end gap-2 border-t border-grey-100 px-6 py-4 dark:border-border">
          <button
            type="button"
            onClick={handleCancel}
            disabled={isSaving}
            className={cn(accountGhostButtonClassName, 'px-4')}
          >
            {t('ACCOUNT_CANCEL', { defaultValue: 'Cancel' })}
          </button>
          <button type="submit" disabled={isSaving} className={accountPrimaryButtonClassName}>
            {t('ACCOUNT_SAVE_CHANGES', { defaultValue: 'Save changes' })}
          </button>
        </div>
      ) : null}
    </form>
  );
}
